import React, { useState } from 'react';
import { useNavigate, Link, Navigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Heart, Users, Calendar, HandHeart, ArrowRight, Gift, Music, BookOpen, HelpCircle } from 'lucide-react';
import { toast } from 'sonner';
import { AppTour } from '../components/AppTour';

// Official Alkimos Baptist Church logo
import ABC_LOGO from '../assets/abc-logo.png';

// Hero banner with subtle stained glass background
const HERO_BANNER = "https://static.prod-images.emergentagent.com/jobs/1a87d92f-638f-47e2-a421-d59693fc520c/images/b41d5f92cc5668e7c528122d66b2ecaeebc747a0a81805bfc613f29b92f981ff.png";

const Logo = ({ size = 'default' }) => {
  const sizeClasses =
    size === 'large' ? 'h-16 w-auto' :
    size === 'hero' ? 'h-32 w-auto sm:h-40' :
    'h-10 w-auto';
  return (
    <img
      src={ABC_LOGO}
      alt="Alkimos Baptist Church"
      className={`${sizeClasses} object-contain`}
    />
  );
};

const features = [
  {
    icon: Users,
    title: 'Community & Growth Groups',
    description: 'Connect with your church family and join growth groups'
  },
  {
    icon: Heart,
    title: 'Prayer Groups',
    description: 'Join prayer circles and support one another through prayer'
  },
  {
    icon: Calendar,
    title: 'Service Planning',
    description: 'View rosters, events, and the church calendar'
  },
  {
    icon: Music,
    title: 'Worship & Music',
    description: 'Access our song library with lyrics and chords'
  },
  {
    icon: HandHeart,
    title: 'Give & Support',
    description: 'Contribute to the church&apos;s mission online'
  },
  {
    icon: BookOpen,
    title: 'Missionary Updates',
    description: 'Follow our supported missionaries and their journeys'
  }
];

const ForgotPasswordForm = ({ onBack }) => {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/forgot-password`, { email });
      setSent(true);
    } catch (error) {
      toast.error('Could not send reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  if (sent) {
    return (
      <div className="mt-4 space-y-4 text-center" data-testid="forgot-password-sent">
        <p className="text-sm text-slate-600">If an account exists for <strong>{email}</strong>, we&apos;ve emailed a link to reset your password. It&apos;s valid for 1 hour.</p>
        <Button variant="outline" className="rounded-full" onClick={onBack} data-testid="forgot-back-btn">Back to sign in</Button>
      </div>
    );
  }
  return (
    <form onSubmit={submit} className="space-y-4 mt-4" data-testid="forgot-password-form">
      <p className="text-sm text-slate-600">Enter your email and we&apos;ll send you a link to choose a new password.</p>
      <Input type="email" placeholder="you@example.com" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="forgot-email-input" />
      <Button type="submit" className="w-full rounded-full bg-church-teal hover:bg-church-teal-dark" disabled={loading} data-testid="forgot-submit-btn">
        {loading ? 'Sending...' : 'Send reset link'}
      </Button>
      <button type="button" onClick={onBack} className="w-full text-sm text-slate-500 hover:text-church-teal" data-testid="forgot-cancel-btn">Back to sign in</button>
    </form>
  );
};

const AuthModal = ({ children, defaultTab = 'login' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [forgot, setForgot] = useState(false);
  const handleOpenChange = (open) => {
    setIsOpen(open);
    if (open) { setActiveTab(defaultTab); setForgot(false); }
  };
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [registerData, setRegisterData] = useState({ name: '', email: '', password: '', phone: '' });

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(loginData.email, loginData.password);
      toast.success('Welcome back!');
      setIsOpen(false);
      navigate('/feed');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(registerData.name, registerData.email, registerData.password, registerData.phone, registerData.accessCode);
      toast.success('Welcome to Alkimos Baptist Church!');
      setIsOpen(false);
      navigate('/feed');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        {children}
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex justify-center mb-4">
            <Logo size="large" />
          </div>
          <DialogTitle className="text-center font-heading text-2xl">
            Welcome to Alkimos Baptist
          </DialogTitle>
          <DialogDescription className="text-center">
            Join our community and grow together in faith
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="login" data-testid="login-tab">Sign In</TabsTrigger>
            <TabsTrigger value="register" data-testid="register-tab">Join Us</TabsTrigger>
          </TabsList>

          <TabsContent value="login">
            {forgot ? <ForgotPasswordForm onBack={() => setForgot(false)} /> : (
            <form onSubmit={handleLogin} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="login-email">Email</Label>
                <Input
                  id="login-email"
                  type="email"
                  placeholder="you@example.com"
                  value={loginData.email}
                  onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                  required
                  data-testid="login-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="login-password">Password</Label>
                <Input
                  id="login-password"
                  type="password"
                  placeholder="••••••••"
                  value={loginData.password}
                  onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                  required
                  data-testid="login-password-input"
                />
              </div>
              <Button type="submit" className="w-full rounded-full bg-church-teal hover:bg-church-teal-dark" disabled={loading} data-testid="login-submit-btn">
                {loading ? 'Signing in...' : 'Sign In'}
              </Button>
              <button type="button" onClick={() => setForgot(true)} className="w-full text-sm text-church-teal hover:underline" data-testid="forgot-password-link">
                Forgot my password
              </button>
            </form>
            )}
          </TabsContent>

          <TabsContent value="register">
            <form onSubmit={handleRegister} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="register-name">Full Name</Label>
                <Input
                  id="register-name"
                  type="text"
                  placeholder="John Smith"
                  value={registerData.name}
                  onChange={(e) => setRegisterData({ ...registerData, name: e.target.value })}
                  required
                  data-testid="register-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="register-email">Email</Label>
                <Input
                  id="register-email"
                  type="email"
                  placeholder="you@example.com"
                  value={registerData.email}
                  onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                  required
                  data-testid="register-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="register-phone">Phone (optional)</Label>
                <Input
                  id="register-phone"
                  type="tel"
                  placeholder="0400 000 000"
                  value={registerData.phone}
                  onChange={(e) => setRegisterData({ ...registerData, phone: e.target.value })}
                  data-testid="register-phone-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="register-password">Password</Label>
                <Input
                  id="register-password"
                  type="password"
                  placeholder="••••••••"
                  value={registerData.password}
                  onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                  required
                  minLength={6}
                  data-testid="register-password-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="register-access-code">Church Access Code</Label>
                <Input
                  id="register-access-code"
                  type="text"
                  placeholder="Provided by the church"
                  value={registerData.accessCode}
                  onChange={(e) => setRegisterData({ ...registerData, accessCode: e.target.value })}
                  required
                  data-testid="register-access-code-input"
                  autoComplete="off"
                />
                <p className="text-xs text-slate-500">
                  Don&apos;t have a code? Ask a Pastor or church staff member.
                </p>
              </div>
              <Button type="submit" className="w-full rounded-full bg-church-teal hover:bg-church-teal-dark" disabled={loading} data-testid="register-submit-btn">
                {loading ? 'Creating account...' : 'Join Our Community'}
              </Button>
            </form>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export const Landing = () => {
  const { isAuthenticated } = useAuth();
  const [runTour, setRunTour] = useState(false);

  if (isAuthenticated) {
    return <Navigate to="/feed" replace />;
  }

  return (
    <div className="min-h-screen" data-testid="landing-page">
      {/* Interactive Tour */}
      <AppTour tourType="landing" run={runTour} onComplete={() => setRunTour(false)} />
      
      {/* Header */}
      <header className="absolute top-0 left-0 right-0 z-10">
        <div className="container-app py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Logo />
            <span className="hidden sm:inline font-heading text-xl font-semibold text-slate-800">
              Alkimos Baptist Church
            </span>
          </div>
          <div className="flex items-center gap-3">
            <Button 
              variant="ghost" 
              className="rounded-full gap-2 text-church-teal hover:text-church-teal-dark hover:bg-church-teal/10"
              onClick={() => setRunTour(true)}
              data-testid="nav-tour"
            >
              <HelpCircle className="w-4 h-4" />
              Tour
            </Button>
            <AuthModal>
              <Button variant="outline" className="rounded-full" data-testid="nav-signin">
                Login
              </Button>
            </AuthModal>
          </div>
        </div>
      </header>

      {/* Hero Section with Subtle Stained Glass Background */}
      <section className="relative pt-28 pb-16 px-4 overflow-hidden">
        {/* Subtle gradient background */}
        <div className="absolute inset-0 bg-gradient-to-b from-church-teal/5 via-white to-church-teal/10" />
        
        {/* Hero banner as subtle background */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-4xl h-[500px] opacity-[0.08] pointer-events-none">
          <img src={HERO_BANNER} alt="" className="w-full h-full object-cover" />
        </div>
        
        <div className="container-app text-center relative z-10">
          {/* Official Alkimos Baptist Church logo */}
          <div className="flex justify-center mb-8 animate-fade-in">
            <div className="relative">
              <Logo size="hero" />
              <div className="absolute -inset-8 bg-church-teal/10 rounded-full blur-3xl -z-10" />
            </div>
          </div>
          
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-medium text-slate-800 mb-6 animate-fade-in" style={{ animationDelay: '0.1s' }}>
            Welcome to<br />
            <span className="text-church-teal">Alkimos Baptist Church</span>
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto mb-8 animate-fade-in" style={{ animationDelay: '0.2s' }}>
            Connect with our community, join growth groups, stay updated with events, 
            and grow together in faith, hope, and love.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in" style={{ animationDelay: '0.3s' }}>
            <AuthModal defaultTab="login">
              <Button size="lg" variant="outline" className="rounded-full border-church-teal text-church-teal hover:bg-church-teal/10 px-10" data-testid="hero-login-btn">
                Login
              </Button>
            </AuthModal>
            <AuthModal defaultTab="register">
              <Button size="lg" className="rounded-full bg-church-teal hover:bg-church-teal-dark px-8 gap-2" data-testid="get-started-btn">
                Join Our Community
                <ArrowRight className="w-4 h-4" />
              </Button>
            </AuthModal>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-16 px-4 bg-slate-50">
        <div className="container-app">
          <h2 className="font-heading text-2xl sm:text-3xl font-semibold text-center text-slate-800 mb-4">
            Everything You Need
          </h2>
          <p className="text-muted-foreground text-center max-w-xl mx-auto mb-12">
            Our church app brings the whole community together in one place
          </p>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {features.map(({ icon: Icon, title, description }) => (
              <Card key={title} className="border-0 shadow-md hover:shadow-lg transition-shadow">
                <CardContent className="pt-6">
                  <div className="w-12 h-12 bg-church-teal/10 rounded-xl flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-church-teal" />
                  </div>
                  <h3 className="font-heading font-semibold text-lg mb-2">{title}</h3>
                  <p className="text-sm text-muted-foreground">{description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Hero Image */}
      <section className="py-16 px-4">
        <div className="container-app">
          <div className="relative rounded-3xl overflow-hidden shadow-2xl">
            <img
              src="https://images.unsplash.com/photo-1627950258197-1e4d8dac1d74?w=1200&h=500&fit=crop"
              alt="Peaceful ocean waves"
              className="w-full h-64 sm:h-80 lg:h-96 object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-church-teal/60 to-transparent" />
            <div className="absolute bottom-8 left-8 right-8 text-white">
              <p className="font-heading text-2xl sm:text-3xl font-medium">
                &ldquo;For where two or three gather in my name, there am I with them.&rdquo;
              </p>
              <p className="mt-2 text-white/80">Matthew 18:20</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-church-sand">
        <div className="container-app text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-medium text-slate-800 mb-4">
            Ready to Connect?
          </h2>
          <p className="text-slate-600 max-w-xl mx-auto mb-8">
            Join our growing community and be part of something meaningful. 
            We'd love to welcome you to the Alkimos Baptist family.
          </p>
          <AuthModal defaultTab="register">
            <Button size="lg" className="rounded-full bg-church-teal hover:bg-church-teal-dark px-8 gap-2" data-testid="cta-join-btn">
              Get Started Today
              <ArrowRight className="w-4 h-4" />
            </Button>
          </AuthModal>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 bg-slate-800 text-white">
        <div className="container-app">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <img src={ABC_LOGO} alt="Alkimos Baptist Church" className="h-10 w-auto object-contain bg-white rounded-md p-1" />
              <span className="font-heading text-lg">Alkimos Baptist Church</span>
            </div>
            <p className="text-white/60 text-sm">
              © {new Date().getFullYear()} Alkimos Baptist Church. Growing together in faith.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
